import React, { useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { toast } from 'react-toastify';
import Attachment from 'src/components/Attachment';
import MessagesRepeater from 'src/components/Messages/MessagesRepeater/MessagesRepeater';
import Empty from 'src/components/Empty';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import { MessageItem } from 'src/components/Messages/types';
import { checkMediaType } from 'src/helpers/CommonHelpers';
import { OfflineUser } from 'src/pages/MagicLinkSignedOutServiceRequest/helpers';
import './ModernizedMessages.scss';

const ALLOWED_MEDIA_TYPES = ['.jpg', '.jpeg', '.png', '.pdf'];

interface ServiceRequestMessagesProps {
  messages: MessageItem[];
  saving: boolean;
  offlineUser: OfflineUser;
  onPostMessage: (chat: { message: string; image?: File[] }) => Promise<void>;
}

const ModernMessages = ({
  messages,
  saving,
  offlineUser,
  onPostMessage,
}: ServiceRequestMessagesProps) => {
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File[]>([]);
  const [lightbox, setLightbox] = useState<{ isOpen: boolean; url: string }>({
    isOpen: false,
    url: '',
  });

  const handleFileSelection = (files: File[]) => {
    if (!checkMediaType((files[0] as any).path, ALLOWED_MEDIA_TYPES)) {
      toast.error('🚫 Invalid file format. Allowed: JPG, PNG, or PDF.', {
        autoClose: 5000,
        position: 'top-center',
      });
      return;
    }

    setSelectedFile(files);
  };

  const handleImageClick = (_index: number, note: MessageItem) => {
    if (!note.fileURL) return;
    const ext = note.fileURL.split(/[#?]/)[0].split('.').pop()?.toLowerCase();

    if (ext === 'pdf' || ext === 'heic') {
      window.open(note.fileURL);
    } else {
      setLightbox({ isOpen: true, url: note.fileURL });
    }
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('🚫 Please enter a message before submitting.', {
        autoClose: 5000,
        position: 'top-center',
      });
      return;
    }

    await onPostMessage({
      message,
      image: selectedFile.length ? selectedFile : undefined,
    });

    setMessage('');
    setSelectedFile([]);
  };

  const user = {
    ...offlineUser,
    roles: offlineUser.roles ?? [{ role: { name: 'HomeOwner' } }],
  };

  return (
    <div className="offline-messages">
      <div className="offline-messages-input">
        <TextareaAutosize
          className="autoincreasetextarea Sroll"
          style={{ width: '100%' }}
          minRows={2}
          maxRows={6}
          placeholder="Enter Your Message Here!"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      <div className="offline-messages-actions">
        <div className="offline-messages-compose-row">
          <LoadingButton
            actionText="Post Message"
            onClick={handleSubmit}
            disabled={!message.trim()}
            isSubmitting={saving}
          />
          <Attachment
            onSelect={handleFileSelection}
            files={selectedFile}
            clearFiles={() => setSelectedFile([])}
            icon={undefined}
            isMultiple={undefined}
          />
        </div>
      </div>
      {messages.length > 0 ? (
        <div className="offline-messages-scroll">
          <MessagesRepeater
            notes={messages}
            user={user}
            onSendEmailNotification={() => {}}
            onImageClicked={handleImageClick}
            tabSelections={null}
          />
        </div>
      ) : (
        <Empty
          headline="No messages yet"
          strapline="Use the form above to send a message to your property manager."
          image={<img alt="No messages yet" src="/assets/empty/notes.svg" />}
          cta={undefined}
          direction={undefined}
          searchIsActive=""
        />
      )}
      {lightbox.isOpen && (
        <ReusableLightbox
          isOpen={lightbox.isOpen}
          onClose={() => setLightbox({ isOpen: false, url: '' })}
          image={lightbox.url}
        />
      )}
    </div>
  );
};

export default ModernMessages;
